import { useContext } from "react";
import { AlarmContext } from "../context/AlarmContext";
import { useState, useEffect, useMemo } from "react";

function LevelTransmitterIndicator({
  tag = "LT-XXXX",

  level = 0,
  totalLevel = 1000,

  inflows = [],
  outflows = [],

  columnX = 0,
  columnY = 0,
  columnWidth = 30,
  columnHeight = 120,

  textX = 0,
  textY = 0,

  showFlow = false,

  highHigh = 0.9,
  lowLow = 0.1,
  backgroundOpacity = 0.6,
  liquidOpacity = 0.8
}) {

  /* ================= FLOW ================= */
  const totalInflow = useMemo(() => {
    return inflows.reduce((a, b) => a + (Number(b) || 0), 0);
  }, [inflows]);

  const totalOutflow = useMemo(() => {
    return outflows.reduce((a, b) => a + (Number(b) || 0), 0);
  }, [outflows]);

  /* ================= ✅ INITIAL LEVEL WITH CATCH-UP ================= */
  const [internalLevel, setInternalLevel] = useState(() => {
    const savedLevel = Number(localStorage.getItem(tag)) || Number(level) || 0;
    const lastTime = Number(localStorage.getItem(`${tag}_time`));

    if (!lastTime) return savedLevel;

    const now = Date.now();
    const deltaSeconds = (now - lastTime) / 1000;

    const inflow = inflows.reduce((a, b) => a + (Number(b) || 0), 0);
    const outflow = outflows.reduce((a, b) => a + (Number(b) || 0), 0);

    let corrected = savedLevel + (inflow - outflow) * deltaSeconds;

    return Math.max(0, Math.min(totalLevel, corrected));
  });












  const [blink, setBlink] = useState(false);

  /* ================= SAVE LEVEL + TIME ================= */
  useEffect(() => {
    localStorage.setItem(tag, internalLevel);
    localStorage.setItem(`${tag}_time`, Date.now());
  }, [internalLevel, tag]);

  

  /* ================= CONTINUOUS UPDATE ================= */
  useEffect(() => {
    const interval = setInterval(() => {
      setInternalLevel(prev => {
        let next = prev + totalInflow - totalOutflow;

        return Math.max(0, Math.min(totalLevel, next));
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [totalInflow, totalOutflow, totalLevel]);

  /* ================= SAFE LEVEL ================= */
  const safeLevel = Number(internalLevel) || 0;

  /* ================= PERCENT ================= */
  const percent = totalLevel > 0
    ? (safeLevel / totalLevel) * 100
    : 0;

  /* ================= ALARM ================= */
  const isHighHigh = percent >= highHigh * 100;
  const isLowLow = percent <= lowLow * 100;

  useEffect(() => {
    if (isHighHigh || isLowLow) {
      const interval = setInterval(() => {
        setBlink(prev => !prev);
      }, 400);
      return () => clearInterval(interval);
    } else {
      setBlink(false);
    }
  }, [isHighHigh, isLowLow]);


  /* 🔥 ALARM ENGINE TAGS */

const { evaluateTag } =
  useContext(AlarmContext);

useEffect(() => {

  evaluateTag(
    tag,
    safeLevel
  );

}, [tag, safeLevel]);



  /* ================= COLOR ================= */
const baseColor = `rgba(0, 229, 255, ${liquidOpacity})`;
const alarmColor = `rgba(255, 0, 0, ${liquidOpacity})`;

const liquidColor =
  (isHighHigh || isLowLow)
    ? (blink ? alarmColor : baseColor)
    : baseColor;

  /* ================= HEIGHT ================= */
  const fillHeight = Math.max(
    0,
    Math.min(columnHeight, (percent / 100) * columnHeight)
  );

  return (
    <>
      {/* ================= COLUMN ================= */}
      <div
        style={{
          position: "absolute",
          left: columnX,
          top: columnY,
          width: columnWidth,
          height: columnHeight,
          border: "2px solid #aaa",
          borderRadius: "6px",
          background: `rgba(17, 17, 17, ${backgroundOpacity})`,
          overflow: "hidden",
          boxShadow: "inset 0 0 6px rgba(0,0,0,0.8)"
        }}
      >

        {/* ================= LIQUID ================= */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            height: fillHeight,
            background: liquidColor,
            transition: "height 0.25s linear"
          }}
        >

          {/* ================= WAVE EFFECT ================= */}
          <svg
            width="100%"
            height="20"
            viewBox="0 0 100 20"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              top: -10,
              left: 0
            }}
          >
            <path fill={liquidColor}>
              <animate
                attributeName="d"
                dur="1.2s"
                repeatCount="indefinite"
                values="
                  M0,10 Q25,0 50,10 T100,10 V20 H0 Z;
                  M0,10 Q25,20 50,10 T100,10 V20 H0 Z;
                  M0,10 Q25,0 50,10 T100,10 V20 H0 Z
                "
              />
            </path>
          </svg>

        </div>
      </div>

      {/* ================= TEXT PANEL ================= */}
      <div
        style={{
          position: "absolute",
          left: textX,
          top: textY,
          color: "white",
          fontSize: "11px",
          background: "rgba(0,0,0,0.65)",
          padding: "0px 6px",
          borderRadius: "4px",
          border: "1px solid #444",
          minWidth: "60px"
        }}
      >
        <div style={{color: "white", paddingRight: "15px", paddingLeft: "0px", fontSize: "11px", background: "blue"}}><b>{tag}</b></div>

        <div>
          {safeLevel.toFixed(1)}
        </div>

        <div>
          {percent.toFixed(1)}%
        </div>

        {showFlow && (
          <>
            <div style={{ color: "lime" }}>
              IN: {totalInflow.toFixed(2)}
            </div>
            <div style={{ color: "orange" }}>
              OUT: {totalOutflow.toFixed(2)}
            </div>
          </>
        )}

        {(isHighHigh || isLowLow) && (
          <div style={{ color: "red", fontWeight: "bold" }}>
            {isHighHigh ? "HH LEVEL" : "LL LEVEL"}
          </div>
        )}
      </div>
    </>
  );
}

export default LevelTransmitterIndicator;